import React, { CSSProperties } from 'react';
import styled, { css } from 'styled-components';

import { wideBreakpoints } from '@r1-frontend/ui-react/components/layouts/wideContainer';

type TCoordinate<T> = Partial<{
    [key in
        | '$top'
        | '$left'
        | '$bottom'
        | '$right'
    ]: T
}>

interface IPosition {
    $position?: Exclude<CSSProperties['position'], 'static'>,
    $zIndex?: CSSProperties['zIndex'],
}

interface ICoordinatePatternPosition extends IPosition, TCoordinate<number> {
    $inset?: never,
}
interface IInsetPatternPosition extends IPosition, TCoordinate<never> {
    $inset: number,
}

export type TNonStaticPosition =
    | ICoordinatePatternPosition
    | IInsetPatternPosition;

export type TNonStaticProps = {
    $united: TNonStaticPosition,
    $desktop?: never,
    $mobile?: never,
} | {
    $united?: never,
    $desktop: TNonStaticPosition,
    $mobile: TNonStaticPosition,
}

const valueOrAuto = (value: number | undefined) => value !== undefined ? `${value}px` : 'auto';

const getPositionFromCompositeValue = ({
    $position = 'absolute',
    $top,
    $left,
    $right,
    $bottom,
    $inset,
    $zIndex,
}: TNonStaticPosition) =>
    css`
        position: ${$position};
        z-index: ${$zIndex || 1};
        top: ${valueOrAuto($inset || $top)};
        left: ${valueOrAuto($inset || $left)};
        right: ${valueOrAuto($inset || $right)};
        bottom: ${valueOrAuto($inset || $bottom)};
    `;

export const getPositionByIncomingProps = (props: TNonStaticProps) => {
    if (props.$united) {
        return getPositionFromCompositeValue(props.$united);
    } else {
        const { $mobile, $desktop } = props;

        return css`
            ${getPositionFromCompositeValue($mobile)}

            @media (min-width: ${wideBreakpoints.mobile}) {
                ${getPositionFromCompositeValue($desktop)}
            }
        `;
    }
};

const NonStaticContainer = styled.div<TNonStaticProps>`
    ${getPositionByIncomingProps};
`;

export const withPosition = <T extends Record<string, unknown>>(
    Component: React.ComponentType<T>,
    props: TNonStaticProps,
) => {
    return styled(Component)<T>`
        ${getPositionByIncomingProps(props)};
    `;
};
export default NonStaticContainer;
